import { z } from "zod/v4";

import { ActionTypeSchema } from "../enums";

export const OptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  // Second line rendered under the label (the survey card's "title +
  // description" row variant). Optional so every existing option — persisted
  // requirements, tool calls, authored nodes — parses unchanged.
  description: z.string().optional(),
});

export const BaseUserInputNodeConfigSchema = z.object({
  model: z.string().optional(),
});

export const UserInputNodeTextPromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("text"),
  });

export const UserInputNodeChoicePromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("choice"),
    options: z.array(OptionSchema),
    /**
     * When true the options are a SET the user assembles — they toggle any
     * number of them and submit once, and the reply carries every value they
     * picked. Absent or false keeps the historical behavior: the options are
     * mutually exclusive and the first click answers the question.
     *
     * Optional rather than defaulted so every existing single-choice config —
     * authored nodes, agent-parked questions, `request-user-input` calls that
     * never mention it — stays valid and unchanged.
     */
    multiSelect: z.boolean().optional(),
  });

export const UserInputNodeConfirmationPromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("confirmation"),
  });

export const UserInputNodeFilePromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("file"),
  });

/**
 * The flat Shopify catalog entities a question can ask the user to pick from
 * their own store. `product` and `collection` answer with Shopify GIDs; the
 * rest answer with the string Shopify itself uses (the tag, the vendor name,
 * the product type).
 */
export const ShopifyEntityKindSchema = z.enum([
  "product",
  "collection",
  "tag",
  "vendor",
  "product_type",
]);

export type ShopifyEntityKind = z.infer<typeof ShopifyEntityKindSchema>;

/**
 * One question of a multi-question survey. `prompt` is the card-header text
 * while the question is active; `options` are the numbered rows (label-only,
 * or title + description when `description` is set); `multiSelect` follows the
 * same optional-not-defaulted contract as the choice config above.
 */
export const SurveyOptionsQuestionSchema = z.object({
  prompt: z.string(),
  options: z.array(OptionSchema).min(1),
  multiSelect: z.boolean().optional(),
});

/**
 * A question whose answer is picked out of the merchant's Shopify catalog
 * rather than from options the agent listed: the agent cannot enumerate a
 * store's products, so the surface opens the matching picker instead of
 * rendering rows. The answer travels in the same `UserInputAnswer` shape —
 * `value` is the GID or the tag / vendor / type string, `label` the title —
 * so nothing downstream of the reply has to know the question was a pick.
 *
 * Distinguished from an options question by `entity` (present) versus
 * `options` (present), not by a discriminator field, so every persisted
 * options question parses exactly as before.
 */
export const SurveyShopifyPickQuestionSchema = z.object({
  prompt: z.string(),
  entity: ShopifyEntityKindSchema,
  multiSelect: z.boolean().optional(),
});

export const SurveyQuestionSchema = z.union([
  SurveyOptionsQuestionSchema,
  SurveyShopifyPickQuestionSchema,
]);

export type SurveyOptionsQuestion = z.infer<typeof SurveyOptionsQuestionSchema>;

export type SurveyShopifyPickQuestion = z.infer<
  typeof SurveyShopifyPickQuestionSchema
>;

export const isShopifyPickQuestion = (
  question: SurveyOptionsQuestion | SurveyShopifyPickQuestion
): question is SurveyShopifyPickQuestion => "entity" in question;

/**
 * Several questions asked as ONE parked requirement, stepped through on a
 * single survey card instead of round-tripping the agent once per question.
 * Single-question asks keep the legacy `choice`/`confirmation` configs — this
 * variant exists only for two or more questions, so every existing consumer of
 * those configs stays untouched.
 */
export const UserInputNodeSurveyPromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("survey"),
    questions: z.array(SurveyQuestionSchema).min(1),
  });

export const UserInputNodeConfigSchema = z.discriminatedUnion("type", [
  UserInputNodeTextPromptConfigSchema,
  UserInputNodeChoicePromptConfigSchema,
  UserInputNodeConfirmationPromptConfigSchema,
  UserInputNodeFilePromptConfigSchema,
  UserInputNodeSurveyPromptConfigSchema,
]);

export const InputRequirementSchema = z.object({
  type: ActionTypeSchema,
  prompt: z.string().optional(),
  variablePath: z.string(),
  userInput: UserInputNodeConfigSchema,
});

// TypeScript types inferred from Zod schemas
export type Option = z.infer<typeof OptionSchema>;

export type UserInputNodeTextPromptConfig = z.infer<
  typeof UserInputNodeTextPromptConfigSchema
>;

export type UserInputNodeChoicePromptConfig = z.infer<
  typeof UserInputNodeChoicePromptConfigSchema
>;

export type UserInputNodeConfirmationPromptConfig = z.infer<
  typeof UserInputNodeConfirmationPromptConfigSchema
>;

export type SurveyQuestion = z.infer<typeof SurveyQuestionSchema>;

export type UserInputNodeSurveyPromptConfig = z.infer<
  typeof UserInputNodeSurveyPromptConfigSchema
>;

export type UserInputNodeConfig = z.infer<typeof UserInputNodeConfigSchema>;
export type InputRequirement = z.infer<typeof InputRequirementSchema>;
